import { memo, useMemo, useState } from "react";
import { BUTTON_LABELS } from "@miplatform/constants";
import { IAccountDetailsResponse, IVerticalTabProps } from "@miplatform/types";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { CustomerDrawsDistributionTab } from "../tabs/CustomerDrawsDistributionTab/CustomerDrawsDistributionTab";
import { CustomerDrawsSetupTab } from "../tabs/CustomerDrawsSetupTab/CustomerDrawsSetupTab";
import { CustomerItemsPricingTab } from "../tabs/CustomerItemsPricingTab/CustomerItemsPricingTab";
import { CustomerBidLineItemsTab } from "../tabs/CustomerLineItemsTabs/CustomerBidLineItemsTab";
import { CustomerOptLineItemsTab } from "../tabs/CustomerLineItemsTabs/CustomerOptLineItemsTab";
import { CustomerPriceLevelTab } from "../tabs/CustomerPriceLevelTab/CustomerPriceLevelTab";
import { CustomerPricingDetailsTabs } from "./CustomerPricingDetails.types";
import {
  CustomerPricingDetailsProvider,
  useCustomerPricingDetailsContext,
} from "./CustomerPricingDetailsProvider";

function CustomerPricingDetailsContent() {
  const {
    accountDetails,
    isViewModelLoading,
    tabFormState,
    handleSave,
    handleCancel,
  } = useCustomerPricingDetailsContext();

  const [selectedTab, setSelectedTab] = useState<string | number | null>(
    CustomerPricingDetailsTabs.PriceLevel
  );

  const handleTabChange = (
    item: IVerticalTabProps<IAccountDetailsResponse>
  ) => {
    if (!item.disabled) {
      setSelectedTab(item.value);
    }
  };

  const tabs = useMemo<IVerticalTabProps<IAccountDetailsResponse>[]>(
    () => [
      {
        label: "Price Level",
        value: CustomerPricingDetailsTabs.PriceLevel,
        viewComponent: CustomerPriceLevelTab,
      },
      {
        label: "Draws Setup",
        value: CustomerPricingDetailsTabs.DrawsSetup,
        viewComponent: CustomerDrawsSetupTab,
      },
      {
        label: "Draws Distribution",
        value: CustomerPricingDetailsTabs.DrawsDistribution,
        viewComponent: CustomerDrawsDistributionTab,
      },
      {
        label: "Bid Line Items",
        value: CustomerPricingDetailsTabs.BidLineItems,
        viewComponent: CustomerBidLineItemsTab,
      },
      {
        label: "Option Line Items",
        value: CustomerPricingDetailsTabs.OptionLineItems,
        viewComponent: CustomerOptLineItemsTab,
      },
      {
        label: "Items Pricing",
        value: CustomerPricingDetailsTabs.ItemsPricing,
        viewComponent: CustomerItemsPricingTab,
      },
    ],
    []
  );

  const isLoading = isViewModelLoading || tabFormState.isLoading;

  const actions = useMemo(() => {
    return {
      cancel: {
        label: BUTTON_LABELS.CANCEL,
        onClick: handleCancel,
      },
      submit: {
        label: BUTTON_LABELS.SAVE,
        onClick: handleSave,
        disabled: !tabFormState.isDirty || !tabFormState.isValid || isLoading,
      },
    };
  }, [tabFormState, isLoading, handleSave, handleCancel]);

  const renderTitle = () => {
    return accountDetails ? `${accountDetails.name}` : "";
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      loading={isLoading}
      bottomActions={actions}
    >
      <VerticalTabsLayout
        tabs={tabs}
        value={selectedTab}
        onTabChange={handleTabChange}
        transparentBackground
        hideNavigationButtons
      />
    </ConfigPageLayout>
  );
}

export const CustomerPricingDetails = memo(() => {
  return (
    <CustomerPricingDetailsProvider>
      <CustomerPricingDetailsContent />
    </CustomerPricingDetailsProvider>
  );
});
